import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import { CTA_LABEL, PROJECTS } from '../../data/site';
import useParallax from '../../hooks/useParallax';
import Container from '../ui/Container';
import LinkButton from '../ui/Button';
import { EASE, staggerChild, staggerParent } from '../ui/Reveal';

const SPRING = { stiffness: 140, damping: 22, mass: 0.5 };

function ScreenshotStack() {
  const reduce = useReducedMotion();
  const { ref, y } = useParallax(34);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, SPRING);
  const smoothY = useSpring(pointerY, SPRING);

  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-5, 5]);
  const rotateX = useTransform(smoothY, [-0.5, 0.5], [4, -4]);
  const backX = useTransform(smoothX, [-0.5, 0.5], [-14, 14]);
  const backY = useTransform(smoothY, [-0.5, 0.5], [-10, 10]);
  const frontX = useTransform(smoothX, [-0.5, 0.5], [18, -18]);
  const frontY = useTransform(smoothY, [-0.5, 0.5], [12, -12]);

  const back = PROJECTS.find((project) => project.slug === 'ssacc');
  const front = PROJECTS.find((project) => project.slug === 'acamanager');

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    pointerX.set((event.clientX - rect.left) / rect.width - 0.5);
    pointerY.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  const handleLeave = () => {
    pointerX.set(0);
    pointerY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={{ y }}
      initial={reduce ? false : { opacity: 0, x: 64 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 1, delay: 0.35, ease: EASE }}
      className="relative"
    >
      <div
        className="relative aspect-[4/3] w-full sm:aspect-[16/10]"
        style={{ perspective: 1400 }}
        onMouseMove={reduce ? undefined : handleMove}
        onMouseLeave={reduce ? undefined : handleLeave}
      >
        <div
          aria-hidden="true"
          className="bg-brand-gradient absolute inset-y-0 left-[10%] right-0 rounded-[32px] shadow-lift sm:left-[16%]"
        />
        <motion.a
          href={back.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`${back.name} 사이트 열기`}
          style={{ x: backX, y: backY, rotateX, rotateY }}
          className="absolute left-0 top-[7%] w-[72%] rounded-2xl bg-white p-1.5 shadow-frame"
        >
          <img src={back.thumb} alt={back.alt} width={900} height={562} className="w-full rounded-xl" />
        </motion.a>
        <motion.a
          href={front.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`${front.name} 사이트 열기`}
          style={{ x: frontX, y: frontY, rotateX, rotateY }}
          className="absolute bottom-[5%] right-[3%] w-[72%] rounded-2xl bg-white p-1.5 shadow-frame"
        >
          <img src={front.thumb} alt={front.alt} width={900} height={562} className="w-full rounded-xl" />
        </motion.a>
      </div>
    </motion.div>
  );
}

function Hero() {
  const reduce = useReducedMotion();

  return (
    <section className="relative overflow-hidden pb-20 pt-10 sm:pb-24 sm:pt-14 lg:pb-28 lg:pt-16">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(180deg,#ebe8ff_0%,#f5f4fb_80%)]"
      />
      <Container>
        <motion.div variants={staggerParent(0.12, 0.05)} initial={reduce ? false : 'hidden'} animate="visible">
          <motion.h1
            variants={staggerChild(0, -48)}
            className="max-w-5xl text-balance text-[2.5rem] font-bold leading-[1.35] tracking-[-0.03em] sm:text-[3.4rem] lg:text-[4.4rem]"
          >
            보기 좋은 화면을 넘어,
            <br className="hidden sm:block" />{' '}
            매일 쓰는 웹서비스를 만듭니다
          </motion.h1>
        </motion.div>

        <div className="mt-10 grid items-end gap-12 lg:mt-14 lg:grid-cols-12 lg:gap-10">
          <motion.div
            className="lg:col-span-5 lg:pb-6"
            variants={staggerParent(0.12, 0.3)}
            initial={reduce ? false : 'hidden'}
            animate="visible"
          >
            <motion.p
              variants={staggerChild(0, -40)}
              className="max-w-[30rem] text-base leading-[1.8] text-ink-soft sm:text-lg"
            >
              홈페이지, 관리 시스템, 포트폴리오. 기획부터 디자인, 개발, 배포까지 한 팀이 맡아 운영하기 쉽게 만듭니다.
            </motion.p>
            <motion.div variants={staggerChild(0, -40)} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <LinkButton to="/contact" size="lg">
                {CTA_LABEL}
                <ArrowRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </LinkButton>
              <LinkButton to="/projects" variant="secondary" size="lg">
                프로젝트 보기
              </LinkButton>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-7">
            <ScreenshotStack />
          </div>
        </div>
      </Container>
    </section>
  );
}

export default Hero;
